import { useCallback, useMemo, useState } from 'react';

/**
 * Stack-based cursor pagination state. The current page's cursor is the top
 * of the stack (or `null` for page 1). `next(cursor)` pushes; `prev()` pops;
 * `reset()` returns to page 1 — call it when the underlying filter changes
 * so the user doesn't see stale rows.
 */
export function useCursorPagination() {
  // Stack of cursors: index 0 = page 2, index 1 = page 3, etc. Page 1 has
  // no cursor at all. Length therefore == zero-indexed page number.
  const [stack, setStack] = useState<string[]>([]);

  const cursor = stack[stack.length - 1] ?? null;
  const page = stack.length + 1;

  const next = useCallback((nextCursor: string) => {
    setStack((s) => [...s, nextCursor]);
  }, []);

  const prev = useCallback(() => {
    setStack((s) => s.slice(0, -1));
  }, []);

  const reset = useCallback(() => {
    setStack([]);
  }, []);

  return useMemo(
    () => ({ cursor, page, hasPrev: stack.length > 0, next, prev, reset }),
    [cursor, page, stack.length, next, prev, reset],
  );
}
